import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { addPatient, fetchPatients } from '../redux/slices/patientSlice';
import { fetchDoctors } from '../redux/slices/doctorSlice';
import Toast from './Toast';

function PatientForm({ onSuccess }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const authState = useSelector((state) => state.auth);
  const doctorId = authState.doctor?.doctorId || authState.doctorId;
  const isAdmin = useSelector((state) => state.admin.isAuthenticated);
  const doctors = useSelector((state) => state.doctors.doctors);
  const patientLoading = useSelector((state) => state.patients.loading);
  const patientError = useSelector((state) => state.patients.error);
  const patientSuccessMessage = useSelector((state) => state.patients.successMessage);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('error'); // 'error' or 'success'
  
  // Debug log
  React.useEffect(() => {
    console.log('Auth State:', authState);
    console.log('Doctor ID:', doctorId);
  }, [authState, doctorId]);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    landlineNumber: '',
    dateOfBirth: '',
    age: '',
    gender: '',
    nidNumber: '',
    address: '',
    doctorId: isAdmin ? '' : (doctorId || ''),
  });

  // Helper function to calculate age from date of birth
  const calculateAge = (dob) => {
    if (!dob) return '';
    const today = new Date();
    const birthDate = new Date(dob);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  // Fetch doctors when admin is viewing the form
  useEffect(() => {
    if (isAdmin) {
      dispatch(fetchDoctors());
    }
  }, [isAdmin, dispatch]);

  // Update doctorId when it changes
  useEffect(() => {
    if (!isAdmin && doctorId) {
      setFormData((prev) => ({
        ...prev,
        doctorId: doctorId,
      }));
    }
  }, [doctorId, isAdmin]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'dateOfBirth') {
      // Auto-calculate age when DOB changes
      const calculatedAge = calculateAge(value);
      setFormData((prev) => ({
        ...prev,
        [name]: value,
        age: calculatedAge,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.firstName.trim()) {
      const msg = 'First name is required';
      setToastMessage(msg);
      setToastType('error');
      return;
    }
    if (!formData.lastName.trim()) {
      const msg = 'Last name is required';
      setToastMessage(msg);
      setToastType('error');
      return;
    }
    if (!formData.phoneNumber.trim()) {
      const msg = 'Phone number is required';
      setToastMessage(msg);
      setToastType('error');
      return;
    }
    
    // For admin, check if doctor is selected
    const finalDoctorId = isAdmin ? parseInt(formData.doctorId) : doctorId;
    if (!finalDoctorId) {
      const msg = isAdmin 
        ? 'Please select a doctor' 
        : `Doctor ID not found. Auth state: ${JSON.stringify(authState.doctor)}`;
      setToastMessage(msg);
      setToastType('error');
      return;
    }
    
    const submitData = {
      firstName: formData.firstName.trim(),
      lastName: formData.lastName.trim(),
      email: formData.email.trim(),
      phoneNumber: formData.phoneNumber.trim() || null,
      landlineNumber: formData.landlineNumber.trim() || null,
      dateOfBirth: formData.dateOfBirth || null,
      age: formData.age ? parseInt(formData.age) : null,
      gender: formData.gender || null,
      nidNumber: formData.nidNumber.trim() || null,
      address: formData.address.trim() || null,
      doctorId: finalDoctorId,
    };
    
    dispatch(addPatient(submitData)).then((result) => {
      if (result.payload && result.payload.patient_id) {
        // Refresh patients list
        if (!isAdmin && doctorId) {
          dispatch(fetchPatients({ doctorId, page: 1, limit: 10 }));
        } else if (isAdmin) {
          dispatch(fetchPatients({ doctorId: null, page: 1, limit: 10 }));
        }
        
        // Navigate to patient details page
        setTimeout(() => {
          navigate(`/patient/${result.payload.patient_id}`, { state: { patient: result.payload } });
          if (onSuccess) {
            onSuccess();
          }
        }, 500);
      } else {
        setFormData({ 
          firstName: '', 
          lastName: '', 
          email: '', 
          phoneNumber: '', 
          landlineNumber: '',
          dateOfBirth: '',
          age: '',
          gender: '',
          nidNumber: '',
          address: '',
          doctorId: isAdmin ? '' : doctorId,
        });
        
        setTimeout(() => {
          if (onSuccess) {
            onSuccess();
          }
        }, 1500);
      }
    });
  };

  // Show error or success toast
  useEffect(() => {
    if (patientError) {
      setToastMessage(patientError);
      setToastType('error');
    } else if (patientSuccessMessage) {
      setToastMessage(patientSuccessMessage);
      setToastType('success');
    }
  }, [patientError, patientSuccessMessage]);

  return (
    <>
      {toastMessage && (
        <Toast 
          message={toastMessage} 
          type={toastType} 
          onClose={() => setToastMessage('')}
        />
      )}
      <form onSubmit={handleSubmit}>
        <h2>Add New Patient</h2>
      <div>
        <label>First name <span style={{color: 'red'}}>*</span></label>
        <input
          type="text"
          name="firstName"
          placeholder="Enter first name"
          value={formData.firstName}
          onChange={handleChange}
          required
        />
      </div>
      <div>
        <label>Last name <span style={{color: 'red'}}>*</span></label>
        <input
          type="text"
          name="lastName"
          placeholder="Enter last name"
          value={formData.lastName}
          onChange={handleChange}
          required
        />
      </div>
      <div>
        <label>Phone number <span style={{color: 'red'}}>*</span></label>
        <input
          type="tel"
          name="phoneNumber"
          placeholder="Enter phone number"
          value={formData.phoneNumber}
          onChange={handleChange}
          required
        />
      </div>
      <div>
        <label>Email</label>
        <input
          type="email"
          name="email"
          placeholder="Enter email"
          value={formData.email}
          onChange={handleChange}
        />
      </div>
      <div>
        <label>NID number</label>
        <input
          type="text"
          name="nidNumber"
          placeholder="Enter NID number"
          value={formData.nidNumber}
          onChange={handleChange}
        />
      </div>
      <div>
        <label>Landline number</label>
        <input
          type="tel"
          name="landlineNumber"
          placeholder="Enter landline number"
          value={formData.landlineNumber}
          onChange={handleChange}
        />
      </div>
      <div>
        <label>Date of birth</label>
        <input
          type="date"
          name="dateOfBirth"
          value={formData.dateOfBirth}
          onChange={handleChange}
        />
      </div>
      <div>
        <label>Age (auto-calculated)</label>
        <input
          type="number"
          name="age"
          placeholder="Auto-calculated"
          value={formData.age}
          onChange={handleChange}
          min="0"
          max="150"
          disabled
        />
      </div>
      {isAdmin && (
        <div>
          <label>Doctor <span style={{color: 'red'}}>*</span></label>
          <select
            name="doctorId"
            value={formData.doctorId}
            onChange={handleChange}
            required
          >
            <option value="">Select a doctor</option>
            {doctors && doctors.map((doctor) => (
              <option key={doctor.doctor_id} value={doctor.doctor_id}>
                {doctor.first_name} {doctor.last_name}
              </option>
            ))}
          </select>
        </div>
      )}
      <div>
        <label>Gender</label>
        <select
          name="gender"
          value={formData.gender}
          onChange={handleChange}
        >
          <option value="">Select gender</option>
          <option value="Male">Male</option>
          <option value="Female">Female</option>
          <option value="Other">Other</option>
        </select>
      </div>
      <div>
        <label>Address</label>
        <textarea
          name="address"
          placeholder="Enter address"
          value={formData.address}
          onChange={handleChange}
          rows="3"
        />
      </div>
      <button type="submit" disabled={patientLoading}>
        {patientLoading ? 'Adding...' : 'Add Patient'}
      </button>
    </form>
    </>
  );
}

export default PatientForm;
